'use client'

import { Editor } from '@tiptap/react'
import {
  ArrowRight,
  CheckSquare,
  Expand,
  FileText,
  List,
  MessageSquare,
  RotateCcw,
  Shrink,
  Smile,
  Zap
} from 'lucide-react'
// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor'
// --- UI Primitives ---
import {
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@deepguide-ai/ui'

const editingOptions = [
  {
    value: 'rephrase',
    label: 'Rephrase',
    description: 'Rephrase for better clarity',
    icon: RotateCcw,
  },
  {
    value: 'fixSpellingAndGrammar',
    label: 'Fix grammar',
    description: 'Fix spelling and grammar errors',
    icon: CheckSquare,
  },
  {
    value: 'shorten',
    label: 'Make shorter',
    description: 'Make content more concise',
    icon: Shrink,
  },
  {
    value: 'extend',
    label: 'Make longer',
    description: 'Add more details and context',
    icon: Expand,
  },
  {
    value: 'simplify',
    label: 'Simplify',
    description: 'Make content easier to understand',
    icon: Zap,
  },
]

const contentOptions = [
  {
    value: 'complete',
    label: 'Continue writing',
    description: 'Continue writing',
    icon: ArrowRight,
  },
  {
    value: 'restructure',
    label: 'Restructure',
    description: 'Improve content organization and flow',
    icon: FileText,
  },
  {
    value: 'keypoints',
    label: 'Key points',
    description: 'Extract key points from content',
    icon: List,
  },
  {
    value: 'summarize',
    label: 'Summarize',
    description: 'Create a comprehensive summary',
    icon: MessageSquare,
  },
  {
    value: 'emojify',
    label: 'Add emojis',
    description: 'Add appropriate emojis',
    icon: Smile,
  },
]

export interface AISelectorCommandsProps {
  editor?: Editor | null
  onSelect: (commandName: string) => void
  onOpenChat?: () => void
}

const suggestedPrompts = [
  {
    value: 'whats-missing',
    label: "What's missing from this section?",
    icon: MessageSquare,
  },
  {
    value: 'add-info',
    label: 'Add information about...',
    icon: FileText,
  },
  {
    value: 'check-consistency',
    label: 'Check for consistency',
    icon: CheckSquare,
  },
  {
    value: 'suggest-improvements',
    label: 'Suggest improvements',
    icon: Zap,
  },
]

export function AISelectorCommands({
  editor: providedEditor,
  onSelect,
  onOpenChat,
}: AISelectorCommandsProps) {
  const { editor } = useTiptapEditor(providedEditor)

  const safeEditingOptions = Array.isArray(editingOptions) ? editingOptions : []
  const safeContentOptions = Array.isArray(contentOptions) ? contentOptions : []

  const handleSelect = (commandName: string) => {
    onSelect(commandName)
  }

  return (
    <CommandList>
      {onOpenChat && (
        <>
          <CommandGroup>
            <CommandItem
              value='open-chat'
              onSelect={onOpenChat}
              className='flex cursor-pointer gap-2 rounded-md px-2 py-1.5 bg-primary/5 hover:bg-primary/10'
            >
              <MessageSquare className='text-primary h-4 w-4 shrink-0' />
              <div className='flex flex-col'>
                <span className='font-medium text-primary'>Open Chat</span>
                <span className='text-muted-foreground text-xs'>
                  Ask questions and get detailed help with context
                </span>
              </div>
            </CommandItem>
          </CommandGroup>
          <CommandSeparator />
        </>
      )}

      <CommandGroup heading='Edit or review selection'>
        {safeEditingOptions.map((option) => (
          <CommandItem
            key={option.value}
            value={option.value}
            onSelect={() => handleSelect(option.value)}
            className='flex cursor-pointer gap-2 rounded-md px-2 py-1.5'
          >
            <option.icon className='text-muted-foreground h-4 w-4 shrink-0' />
            <div className='flex flex-col'>
              <span className='font-medium'>{option.label}</span>
            </div>
          </CommandItem>
        ))}
      </CommandGroup>

      <CommandSeparator />

      <CommandGroup heading='Transform content'>
        {safeContentOptions.map((option) => (
          <CommandItem
            key={option.value}
            value={option.value}
            onSelect={() => handleSelect(option.value)}
            className='flex cursor-pointer gap-2 rounded-md px-2 py-1.5'
          >
            <option.icon className='text-muted-foreground h-4 w-4 shrink-0' />
            <div className='flex flex-col'>
              <span className='font-medium'>{option.label}</span>
            </div>
          </CommandItem>
        ))}
      </CommandGroup>
    </CommandList>
  )
}
